import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button, Slider, Select, Typography, InputNumber, Switch, Divider } from 'antd';
import api from '../../../api/client';
import {
    AlignLeftOutlined,
    AlignCenterOutlined,
    AlignRightOutlined,
    ItalicOutlined,
    UploadOutlined,
} from '@ant-design/icons';

const { Text } = Typography;

// Weights with labels
const WEIGHTS = [
    { value: 100, label: '100 Thin' },
    { value: 200, label: '200 Extra Light' },
    { value: 300, label: '300 Light' },
    { value: 400, label: '400 Regular' },
    { value: 500, label: '500 Medium' },
    { value: 600, label: '600 Semi Bold' },
    { value: 700, label: '700 Bold' },
    { value: 800, label: '800 Extra Bold' },
    { value: 900, label: '900 Black' },
];

// Google Fonts
const GOOGLE_FONTS = [
    'Inter', 'Roboto', 'Space Grotesk', 'Space Mono', 'DM Sans', 'DM Mono',
    'Bebas Neue', 'Oswald', 'Barlow Condensed', 'Anton', 'Montserrat',
    'Playfair Display', 'Roboto Mono', 'JetBrains Mono', 'IBM Plex Sans',
    'IBM Plex Mono', 'Archivo Black', 'Outfit', 'Manrope', 'Syne',
];

// Load Google Fonts via stylesheet link
const loadedGoogleLinks = new Set();
const loadGoogleFont = (family) => {
    if (loadedGoogleLinks.has(family)) return;
    loadedGoogleLinks.add(family);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${family.replace(/ /g, '+')}:wght@100;200;300;400;500;600;700;800;900&display=swap`;
    document.head.appendChild(link);
};
// Pre-load all Google fonts
GOOGLE_FONTS.forEach(f => loadGoogleFont(f));

// Persistent set of custom fonts loaded via file upload (survives re-renders)
// { familyName: string }
const registeredCustomFonts = [];

// Derive a clean font name from filename
// "Roboto-Bold.ttf" -> "Roboto Bold"
const deriveFontName = (filename) => {
    let name = filename.replace(/\.(ttf|woff2?|otf)$/i, '');
    name = name.replace(/[-_]/g, ' ');
    return name;
};

const TypeControlsPanel = ({ visible, textObject, onUpdate, onClose }) => {
    const [fontFamily, setFontFamily] = useState('Inter');
    const [fontSize, setFontSize] = useState(48);
    const [fontWeight, setFontWeight] = useState(700);
    const [fontStyle, setFontStyle] = useState('normal');
    const [letterSpacing, setLetterSpacing] = useState(0);
    const [lineHeight, setLineHeight] = useState(1.1);
    const [textAlign, setTextAlign] = useState('left');
    const [textTransform, setTextTransform] = useState('none');
    const [fill, setFill] = useState('#ffffff');
    const [opacity, setOpacity] = useState(1);
    const [customFonts, setCustomFonts] = useState(() => [...registeredCustomFonts]);
    const fileInputRef = useRef(null);

    // Text effects state
    const [strokeEnabled, setStrokeEnabled] = useState(false);
    const [strokeColor, setStrokeColor] = useState('#000000');
    const [strokeWidth, setStrokeWidth] = useState(2);
    const [shadowEnabled, setShadowEnabled] = useState(false);
    const [shadowColor, setShadowColor] = useState('rgba(0,0,0,0.6)');
    const [shadowOffsetX, setShadowOffsetX] = useState(4);
    const [shadowOffsetY, setShadowOffsetY] = useState(4);
    const [shadowBlur, setShadowBlur] = useState(8);
    const [textBgEnabled, setTextBgEnabled] = useState(false);
    const [textBgColor, setTextBgColor] = useState('#a78bfa');

    // Sync state from selected text object
    useEffect(() => {
        if (!textObject) return;
        setFontFamily(textObject.fontFamily || 'Inter');
        setFontSize(textObject.fontSize || 48);
        setFontWeight(textObject.fontWeight || 700);
        setFontStyle(textObject.fontStyle || 'normal');
        setLetterSpacing(textObject.charSpacing != null ? Math.round(textObject.charSpacing / 10) : 0);
        setLineHeight(textObject.lineHeight || 1.1);
        setTextAlign(textObject.textAlign || 'left');
        setFill(textObject.fill || '#ffffff');
        setOpacity(textObject.opacity != null ? textObject.opacity : 1);
        setTextTransform(textObject._textTransform || 'none');
        // Sync effects state
        setStrokeEnabled(!!textObject.stroke && textObject.strokeWidth > 0);
        setStrokeColor(textObject.stroke || '#000000');
        setStrokeWidth(textObject.strokeWidth || 2);
        const sh = textObject.shadow;
        setShadowEnabled(!!sh);
        if (sh) {
            setShadowColor(sh.color || 'rgba(0,0,0,0.6)');
            setShadowOffsetX(sh.offsetX || 4);
            setShadowOffsetY(sh.offsetY || 4);
            setShadowBlur(sh.blur || 8);
        }
        setTextBgEnabled(!!textObject.textBackgroundColor);
        setTextBgColor(textObject.textBackgroundColor || '#a78bfa');
    }, [textObject]);

    // Fetch custom font families from backend to populate dropdown
    useEffect(() => {
        (async () => {
            try {
                const res = await api.get('/font-families');
                const families = Array.isArray(res.data) ? res.data : [];
                for (const fam of families) {
                    const familyName = fam.metadata?.familyName || fam.name || 'Untitled';
                    if (registeredCustomFonts.some(f => f.familyName === familyName)) continue;
                    const entry = { familyName };
                    registeredCustomFonts.push(entry);
                    setCustomFonts(prev => [...prev, entry]);
                }
            } catch (err) {
                console.error('[TypeControls] Failed to fetch font families:', err);
            }
        })();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const update = useCallback((prop, value) => {
        onUpdate?.(prop, value);
    }, [onUpdate]);

    // Handle custom font file upload
    const handleFontUpload = async (e) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        for (const file of files) {
            const familyName = deriveFontName(file.name);

            // Check if already loaded
            if (registeredCustomFonts.some(f => f.familyName === familyName)) continue;

            try {
                const buffer = await file.arrayBuffer();
                const font = new FontFace(familyName, buffer);
                const loaded = await font.load();
                document.fonts.add(loaded);
                await document.fonts.ready;

                const entry = { familyName };
                registeredCustomFonts.push(entry);
                setCustomFonts(prev => [...prev, entry]);
            } catch (err) {
                console.warn(`Failed to load font "${familyName}" from file:`, err);
            }
        }

        // Reset file input so same file can be re-selected
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleFontFamily = (val) => {
        setFontFamily(val);
        // Only load Google font if it's not a custom font
        if (!registeredCustomFonts.some(f => f.familyName === val)) {
            loadGoogleFont(val);
        }
        update('fontFamily', val);
    };

    const handleFontSize = (val) => {
        if (val == null) return;
        setFontSize(val);
        update('fontSize', val);
    };

    const handleFontWeight = (val) => {
        setFontWeight(val);
        update('fontWeight', val);
    };

    const handleFontStyle = () => {
        const newStyle = fontStyle === 'italic' ? 'normal' : 'italic';
        setFontStyle(newStyle);
        update('fontStyle', newStyle);
    };

    const handleLetterSpacing = (val) => {
        setLetterSpacing(val);
        update('charSpacing', val * 10);
    };

    const handleLineHeight = (val) => {
        setLineHeight(val);
        update('lineHeight', val);
    };

    const handleTextAlign = (val) => {
        setTextAlign(val);
        update('textAlign', val);
    };

    const handleTextTransform = (val) => {
        setTextTransform(val);
        update('textTransform', val);
    };

    const handleFill = (val) => {
        setFill(val);
        update('fill', val);
    };

    const handleOpacity = (val) => {
        setOpacity(val);
        update('opacity', val);
    };

    // ─── Effects handlers ───
    const handleStrokeToggle = (on) => {
        setStrokeEnabled(on);
        if (on) {
            update('stroke', strokeColor);
            update('strokeWidth', strokeWidth);
        } else {
            update('stroke', null);
            update('strokeWidth', 0);
        }
    };
    const handleStrokeColor = (val) => {
        setStrokeColor(val);
        if (strokeEnabled) update('stroke', val);
    };
    const handleStrokeWidth = (val) => {
        setStrokeWidth(val);
        if (strokeEnabled) update('strokeWidth', val);
    };
    const handleShadowToggle = (on) => {
        setShadowEnabled(on);
        if (on) {
            update('shadow', { color: shadowColor, offsetX: shadowOffsetX, offsetY: shadowOffsetY, blur: shadowBlur });
        } else {
            update('shadow', null);
        }
    };
    const updateShadow = (patch) => {
        const vals = { color: shadowColor, offsetX: shadowOffsetX, offsetY: shadowOffsetY, blur: shadowBlur, ...patch };
        if (patch.color !== undefined) setShadowColor(patch.color);
        if (patch.offsetX !== undefined) setShadowOffsetX(patch.offsetX);
        if (patch.offsetY !== undefined) setShadowOffsetY(patch.offsetY);
        if (patch.blur !== undefined) setShadowBlur(patch.blur);
        if (shadowEnabled) update('shadow', vals);
    };
    const handleTextBgToggle = (on) => {
        setTextBgEnabled(on);
        update('textBackgroundColor', on ? textBgColor : '');
    };
    const handleTextBgColor = (val) => {
        setTextBgColor(val);
        if (textBgEnabled) update('textBackgroundColor', val);
    };

    const sliderRow = (label, min, max, value, onChange, step = 1) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <Text style={{ color: '#8b949e', fontSize: 11, minWidth: 72, whiteSpace: 'nowrap' }}>{label}</Text>
            <Slider min={min} max={max} step={step} value={value} onChange={onChange}
                style={{ flex: 1, margin: 0 }}
                styles={{ track: { background: '#a78bfa' }, rail: { background: '#30363d' }, handle: { borderColor: '#a78bfa', width: 12, height: 12 } }}
            />
            <Text style={{ color: '#a78bfa', fontSize: 11, fontFamily: 'monospace', minWidth: 32, textAlign: 'right' }}>
                {step < 1 ? value.toFixed(step < 0.1 ? 2 : 1) : value}
            </Text>
        </div>
    );

    // Build font options: custom fonts first, then Google fonts
    const fontOptions = (() => {
        const groups = [];
        if (customFonts.length > 0) {
            groups.push({
                label: <span style={{ color: '#a78bfa', fontSize: 10, fontWeight: 700, letterSpacing: 0.5 }}>CUSTOM FONTS</span>,
                options: customFonts.map(f => ({
                    value: f.familyName,
                    label: <span style={{ color: '#c9d1d9' }}>{f.familyName}</span>,
                })),
            });
        }
        groups.push({
            label: <span style={{ color: '#6e7681', fontSize: 10, fontWeight: 700, letterSpacing: 0.5 }}>GOOGLE FONTS</span>,
            options: GOOGLE_FONTS.map(f => ({
                value: f,
                label: <span style={{ fontFamily: f }}>{f}</span>,
            })),
        });
        return groups;
    })();

    if (!visible) return null;

    return (
        <div
            style={{
                position: 'absolute',
                top: 48,
                right: 10,
                width: 270,
                zIndex: 90,
                background: 'rgba(13, 17, 23, 0.97)',
                border: '1px solid #30363d',
                borderRadius: 10,
                boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                overflow: 'visible',
                pointerEvents: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
        >
            {/* Hidden file input */}
            <input
                ref={fileInputRef}
                type="file"
                accept=".ttf,.woff,.woff2,.otf"
                multiple
                style={{ display: 'none' }}
                onChange={handleFontUpload}
            />

            {/* Header */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '10px 14px',
                borderBottom: '1px solid #30363d',
                background: '#161b22',
                borderRadius: '10px 10px 0 0',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 14, fontWeight: 700, fontFamily: 'serif' }}>T</span>
                    <span style={{ color: '#c9d1d9', fontWeight: 600, fontSize: 13 }}>Type</span>
                </div>
                <Button
                    type="text"
                    size="small"
                    onClick={onClose}
                    style={{ color: '#8b949e', fontSize: 11, padding: '2px 6px' }}
                >
                    ✕
                </Button>
            </div>

            {/* Controls */}
            <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 14, maxHeight: 480, overflowY: 'auto' }}>
                {/* Font Family */}
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Text style={{ color: '#a78bfa', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Font</Text>
                        <Button
                            type="text"
                            size="small"
                            icon={<UploadOutlined />}
                            onClick={() => fileInputRef.current?.click()}
                            style={{ color: '#6e7681', fontSize: 10, padding: '2px 6px' }}
                        >
                            Add Font
                        </Button>
                    </div>
                    <Select
                        value={fontFamily}
                        onChange={handleFontFamily}
                        showSearch
                        style={{ width: '100%', marginTop: 6 }}
                        optionFilterProp="label"
                        getPopupContainer={() => document.body}
                        virtual={false}
                        options={fontOptions}
                    />
                </div>

                {/* Font Size + Weight */}
                <div>
                    <Text style={{ color: '#a78bfa', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Size & Weight</Text>
                    <div style={{ marginTop: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                            <Text style={{ color: '#8b949e', fontSize: 11, minWidth: 72 }}>Size</Text>
                            <InputNumber
                                min={8} max={400}
                                value={fontSize}
                                onChange={handleFontSize}
                                size="small"
                                style={{ width: 70, background: '#21262d', borderColor: '#30363d', color: '#c9d1d9' }}
                            />
                            <Text style={{ color: '#6e7681', fontSize: 10 }}>px</Text>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                            <Text style={{ color: '#8b949e', fontSize: 11, minWidth: 72 }}>Weight</Text>
                            <Select
                                value={fontWeight}
                                onChange={handleFontWeight}
                                size="small"
                                style={{ flex: 1 }}
                                getPopupContainer={() => document.body}
                                options={WEIGHTS}
                            />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                            <Text style={{ color: '#8b949e', fontSize: 11, minWidth: 72 }}>Style</Text>
                            <Button
                                size="small"
                                icon={<ItalicOutlined />}
                                type={fontStyle === 'italic' ? 'primary' : 'text'}
                                onClick={handleFontStyle}
                                style={{
                                    color: fontStyle === 'italic' ? undefined : '#6e7681',
                                    background: fontStyle === 'italic' ? '#a78bfa' : undefined,
                                    borderColor: fontStyle === 'italic' ? '#a78bfa' : undefined,
                                }}
                            />
                        </div>
                    </div>
                </div>

                {/* Spacing */}
                <div>
                    <Text style={{ color: '#a78bfa', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Spacing</Text>
                    <div style={{ marginTop: 8 }}>
                        {sliderRow('Tracking', -50, 200, letterSpacing, handleLetterSpacing)}
                        {sliderRow('Leading', 0.5, 3.0, lineHeight, handleLineHeight, 0.05)}
                    </div>
                </div>

                {/* Alignment & Transform */}
                <div>
                    <Text style={{ color: '#a78bfa', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Alignment</Text>
                    <div style={{ display: 'flex', gap: 4, marginTop: 8 }}>
                        {[
                            { val: 'left', icon: <AlignLeftOutlined /> },
                            { val: 'center', icon: <AlignCenterOutlined /> },
                            { val: 'right', icon: <AlignRightOutlined /> },
                        ].map(({ val, icon }) => (
                            <Button
                                key={val}
                                size="small"
                                icon={icon}
                                type={textAlign === val ? 'primary' : 'text'}
                                onClick={() => handleTextAlign(val)}
                                style={{
                                    color: textAlign === val ? undefined : '#6e7681',
                                    background: textAlign === val ? '#a78bfa' : undefined,
                                    borderColor: textAlign === val ? '#a78bfa' : undefined,
                                }}
                            />
                        ))}
                    </div>
                </div>

                <div>
                    <Text style={{ color: '#a78bfa', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Transform</Text>
                    <div style={{ display: 'flex', gap: 4, marginTop: 8 }}>
                        {[
                            { val: 'none', label: 'Aa' },
                            { val: 'uppercase', label: 'AA' },
                            { val: 'lowercase', label: 'aa' },
                        ].map(({ val, label }) => (
                            <Button
                                key={val}
                                size="small"
                                type={textTransform === val ? 'primary' : 'text'}
                                onClick={() => handleTextTransform(val)}
                                style={{
                                    color: textTransform === val ? undefined : '#6e7681',
                                    background: textTransform === val ? '#a78bfa' : undefined,
                                    borderColor: textTransform === val ? '#a78bfa' : undefined,
                                    fontWeight: 600,
                                    fontSize: 12,
                                    minWidth: 36,
                                }}
                            >
                                {label}
                            </Button>
                        ))}
                    </div>
                </div>

                {/* Color */}
                <div>
                    <Text style={{ color: '#a78bfa', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Color</Text>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                        <div
                            style={{
                                width: 28, height: 28, borderRadius: 6,
                                background: fill,
                                border: '2px solid #30363d',
                                cursor: 'pointer',
                                flexShrink: 0,
                            }}
                            onClick={() => {
                                const input = document.createElement('input');
                                input.type = 'color';
                                input.value = fill;
                                input.style.cssText = 'position:absolute;opacity:0;pointer-events:none';
                                document.body.appendChild(input);
                                input.addEventListener('input', (e) => handleFill(e.target.value));
                                input.addEventListener('change', () => document.body.removeChild(input));
                                input.click();
                            }}
                        />
                        <Text style={{ color: '#6e7681', fontSize: 11, fontFamily: 'monospace' }}>{fill}</Text>
                    </div>
                    <div style={{ marginTop: 8 }}>
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            {['#ffffff', '#000000', '#c9d1d9', '#8b949e', '#f43f5e', '#fbbf24', '#34d399', '#60a5fa', '#a78bfa', '#f472b6', '#d5f667'].map(c => (
                                <div
                                    key={c}
                                    onClick={() => handleFill(c)}
                                    style={{
                                        width: 20, height: 20, borderRadius: 4,
                                        background: c,
                                        border: fill === c ? '2px solid #a78bfa' : '1px solid #30363d',
                                        cursor: 'pointer',
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                </div>

                {/* Opacity */}
                <div>
                    {sliderRow('Opacity', 0, 1, opacity, handleOpacity, 0.01)}
                </div>

                {/* ─── Text Effects ─── */}
                <Divider style={{ margin: '4px 0', borderColor: '#30363d' }} />
                <Text style={{ color: '#a78bfa', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Effects</Text>

                {/* Stroke / Outline */}
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                        <Text style={{ color: '#c9d1d9', fontSize: 11, fontWeight: 600 }}>Stroke</Text>
                        <Switch size="small" checked={strokeEnabled} onChange={handleStrokeToggle}
                            style={{ background: strokeEnabled ? '#a78bfa' : '#30363d' }} />
                    </div>
                    {strokeEnabled && (
                        <div>
                            {sliderRow('Width', 0.5, 20, strokeWidth, handleStrokeWidth, 0.5)}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                                <Text style={{ color: '#8b949e', fontSize: 11, minWidth: 72 }}>Color</Text>
                                <div
                                    style={{
                                        width: 24, height: 24, borderRadius: 4,
                                        background: strokeColor,
                                        border: '2px solid #30363d',
                                        cursor: 'pointer', flexShrink: 0,
                                    }}
                                    onClick={() => {
                                        const input = document.createElement('input');
                                        input.type = 'color'; input.value = strokeColor;
                                        input.style.cssText = 'position:absolute;opacity:0;pointer-events:none';
                                        document.body.appendChild(input);
                                        input.addEventListener('input', (e) => handleStrokeColor(e.target.value));
                                        input.addEventListener('change', () => document.body.removeChild(input));
                                        input.click();
                                    }}
                                />
                                <Text style={{ color: '#6e7681', fontSize: 11, fontFamily: 'monospace' }}>{strokeColor}</Text>
                            </div>
                        </div>
                    )}
                </div>

                {/* Shadow */}
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                        <Text style={{ color: '#c9d1d9', fontSize: 11, fontWeight: 600 }}>Shadow</Text>
                        <Switch size="small" checked={shadowEnabled} onChange={handleShadowToggle}
                            style={{ background: shadowEnabled ? '#a78bfa' : '#30363d' }} />
                    </div>
                    {shadowEnabled && (
                        <div>
                            {sliderRow('Offset X', -40, 40, shadowOffsetX, (v) => updateShadow({ offsetX: v }))}
                            {sliderRow('Offset Y', -40, 40, shadowOffsetY, (v) => updateShadow({ offsetY: v }))}
                            {sliderRow('Blur', 0, 40, shadowBlur, (v) => updateShadow({ blur: v }))}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                                <Text style={{ color: '#8b949e', fontSize: 11, minWidth: 72 }}>Color</Text>
                                <div
                                    style={{
                                        width: 24, height: 24, borderRadius: 4,
                                        background: shadowColor,
                                        border: '2px solid #30363d',
                                        cursor: 'pointer', flexShrink: 0,
                                    }}
                                    onClick={() => {
                                        const input = document.createElement('input');
                                        input.type = 'color'; input.value = shadowColor.startsWith('#') ? shadowColor : '#000000';
                                        input.style.cssText = 'position:absolute;opacity:0;pointer-events:none';
                                        document.body.appendChild(input);
                                        input.addEventListener('input', (e) => updateShadow({ color: e.target.value }));
                                        input.addEventListener('change', () => document.body.removeChild(input));
                                        input.click();
                                    }}
                                />
                            </div>
                        </div>
                    )}
                </div>

                {/* Text Background */}
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                        <Text style={{ color: '#c9d1d9', fontSize: 11, fontWeight: 600 }}>Highlight</Text>
                        <Switch size="small" checked={textBgEnabled} onChange={handleTextBgToggle}
                            style={{ background: textBgEnabled ? '#a78bfa' : '#30363d' }} />
                    </div>
                    {textBgEnabled && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Text style={{ color: '#8b949e', fontSize: 11, minWidth: 72 }}>Color</Text>
                            <div style={{ display: 'flex', gap: 4 }}>
                                {['#a78bfa', '#f43f5e', '#fbbf24', '#34d399', '#60a5fa', '#f472b6', '#000000', '#ffffff'].map(c => (
                                    <div
                                        key={c}
                                        onClick={() => handleTextBgColor(c)}
                                        style={{
                                            width: 20, height: 20, borderRadius: 4,
                                            background: c,
                                            border: textBgColor === c ? '2px solid #a78bfa' : '1px solid #30363d',
                                            cursor: 'pointer',
                                        }}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TypeControlsPanel;
